/**
 * Season schedule generator.
 *
 * Distinct from `schedulerEngine.ts` (which is the compressed-tournament
 * round-robin). Seasons spread games over weeks: each weeknight plays a
 * subset of pairings until the regular season window is filled.
 *
 * Pure logic — returns proposed Games + Slots without writing to the store.
 */

import type {
  Division,
  Id,
  Iso8601,
  Rink,
  SeasonSettings,
  Team,
  Tournament,
  Weekday,
} from '../models';
import { roundRobinPairings, type ProposedGame, type ProposedSlot, type SchedulerWarning } from './schedulerEngine';

const WEEKDAY_NUM: Record<Weekday, number> = {
  sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6,
};

export interface SeasonScheduleInput {
  tournament: Tournament;
  divisions: Division[];
  teams: Team[];
  rinks: Rink[];
}

export interface SeasonScheduleResult {
  games: ProposedGame[];
  slots: ProposedSlot[];
  warnings: SchedulerWarning[];
  weeksGenerated: number;
}

const id = (p: string) => `${p}-${Math.random().toString(36).slice(2, 10)}`;

/** Build the list of game-night dates between start and end, honouring weekdays + blackouts. */
export function buildGameNights(
  start: Iso8601,
  end: Iso8601,
  season: SeasonSettings,
): string[] {
  const startMs = new Date(start).getTime();
  const endMs = new Date(end).getTime();
  const blackouts = new Set(season.blackoutDates.map((d) => d.slice(0, 10)));
  const weekdayNums = new Set(season.weekdays.map((w) => WEEKDAY_NUM[w]));
  const out: string[] = [];
  for (let t = startMs; t <= endMs; t += 86_400_000) {
    const d = new Date(t);
    if (!weekdayNums.has(d.getUTCDay())) continue;
    const ymd = d.toISOString().slice(0, 10);
    if (blackouts.has(ymd)) continue;
    out.push(ymd);
  }
  return out;
}

/**
 * Generate a season schedule. Round-robin pairings spread across game nights;
 * each night uses available rinks in parallel until the queue is empty.
 *
 * Strategy:
 *  1. Build the round-robin queue per division.
 *  2. Walk game nights in order. For each, place as many games as we have
 *     rinks while honouring "no team plays itself twice in one night".
 *  3. Stop when queue is empty or game nights are exhausted.
 */
export function generateSeasonSchedule(input: SeasonScheduleInput): SeasonScheduleResult {
  const { tournament, divisions, teams, rinks } = input;
  const warnings: SchedulerWarning[] = [];
  if (tournament.format !== 'season') {
    warnings.push({ code: 'no_teams', message: 'generateSeasonSchedule called on a non-season competition' });
    return { games: [], slots: [], warnings, weeksGenerated: 0 };
  }
  if (rinks.length === 0) {
    warnings.push({ code: 'no_rinks', message: 'No rinks available — add at least one rink.' });
    return { games: [], slots: [], warnings, weeksGenerated: 0 };
  }
  if (teams.length === 0) {
    warnings.push({ code: 'no_teams', message: 'No teams to schedule yet.' });
    return { games: [], slots: [], warnings, weeksGenerated: 0 };
  }

  const season = tournament.settings.season ?? {
    weekdays: ['wed' as Weekday],
    firstPuckTime: '18:00',
    gamesPerRinkPerNight: 3,
    blackoutDates: [],
    rosterIntake: 'later' as const,
    registration: {
      enabled: false,
      intakeType: 'individual' as const,
      feeCents: 5000,
      requireWaiver: true,
      autoApprove: false,
    },
    halfSplit: false,
  };
  const regularEnd = season.regularSeasonEndsAt ?? tournament.endsAt;
  const nights = buildGameNights(tournament.startsAt, regularEnd, season);
  if (nights.length === 0) {
    warnings.push({ code: 'no_teams', message: 'No game nights match the configured weekdays + blackouts.' });
    return { games: [], slots: [], warnings, weeksGenerated: 0 };
  }

  // Pairing queue per division.
  type Match = { divisionId: Id; home: Team; away: Team };
  const queue: Match[] = [];
  for (const div of divisions) {
    const ts = teams.filter((t) => t.divisionId === div.id);
    if (ts.length < 2) continue;
    if (ts.length % 2 === 1) {
      warnings.push({
        code: 'odd_team_count',
        message: `Division "${div.name}" has an odd number of teams.`,
        context: { divisionId: div.id },
      });
    }
    for (const [home, away] of roundRobinPairings(ts)) {
      queue.push({ divisionId: div.id, home, away });
    }
  }

  const gameLen = tournament.settings.scoring.gameLengthMinutes;
  const turnover = tournament.settings.scheduling.rinkTurnoverMinutes;
  const slots: ProposedSlot[] = [];
  const games: ProposedGame[] = [];

  const firstPuckTime = /^\d{2}:\d{2}$/.test(season.firstPuckTime) ? season.firstPuckTime : '18:00';
  const gamesPerRinkPerNight = Math.max(1, season.gamesPerRinkPerNight || 3);

  let weeksUsed = 0;
  for (const ymd of nights) {
    if (queue.length === 0) break;
    weeksUsed += 1;
    const playedTonight = new Set<Id>();
    // Per-rink time cursor for this night.
    const rinkCursor = new Map<Id, number>();
    const rinkGamesPlaced = new Map<Id, number>();
    for (const r of rinks) {
      rinkCursor.set(r.id, new Date(`${ymd}T${firstPuckTime}:00Z`).getTime());
      rinkGamesPlaced.set(r.id, 0);
    }
    let placedThisNight = true;
    while (placedThisNight) {
      placedThisNight = false;
      for (const [rinkId, cursor] of rinkCursor.entries()) {
        if ((rinkGamesPlaced.get(rinkId) ?? 0) >= gamesPerRinkPerNight) continue;
        const idx = queue.findIndex((m) => !playedTonight.has(m.home.id) && !playedTonight.has(m.away.id));
        if (idx === -1) continue;
        const match = queue.splice(idx, 1)[0];
        playedTonight.add(match.home.id);
        playedTonight.add(match.away.id);
        const startMs = cursor;
        const endMs = startMs + gameLen * 60_000;
        const startsAt = new Date(startMs).toISOString();
        const endsAt = new Date(endMs).toISOString();
        const slot: ProposedSlot = {
          id: id('slot'),
          tournamentId: tournament.id,
          rinkId,
          startsAt,
          endsAt,
        };
        slots.push(slot);
        games.push({
          id: id('game'),
          tournamentId: tournament.id,
          divisionId: match.divisionId,
          scheduleSlotId: slot.id,
          homeTeamId: match.home.id,
          awayTeamId: match.away.id,
          status: 'scheduled',
          displayLabel: `${match.home.name} vs ${match.away.name}`,
          groupLabel: `Week ${weeksUsed}`,
        });
        rinkCursor.set(rinkId, endMs + turnover * 60_000);
        rinkGamesPlaced.set(rinkId, (rinkGamesPlaced.get(rinkId) ?? 0) + 1);
        placedThisNight = true;
      }
    }
  }

  if (queue.length > 0) {
    warnings.push({
      code: 'late_finish',
      message: `${queue.length} games could not be placed in the regular-season window — extend the date range or add rinks.`,
    });
  }

  return { games, slots, warnings, weeksGenerated: weeksUsed };
}
